import useStyles from 'isomorphic-style-loader/useStyles';
import { useCallback, useRef, useState } from 'react';
import {
  useEventListener,
  useIsomorphicLayoutEffect,
  useResizeObserver,
} from 'usehooks-ts';

import { useComputedPagePaddingTopPx } from '@app/offset-anchor';
import ContextPanel, { useContextPanel } from '@components/context-panel';
import { DEFAULT_PANEL_WIDTH } from '@components/context-panel/context-panel-outlet';

import { usePageContext } from './page-context';
import s from './page.scss';

/**
 * Internal component that bridges the page-scope context panel state to a
 * fixed-position `ContextPanel`. Handles dynamic top-edge computation for both
 * sticky and non-sticky Page headers, and sets a CSS custom property on the
 * page container for push-mode content offset.
 */
export const PageContextPanel = () => {
  useStyles(s);

  const { containerRef, headerRef } = usePageContext();
  const { isOpen, options } = useContextPanel('page');
  const [panelTop, setPanelTop] = useState<number | undefined>();
  const pageOffsetTop = useComputedPagePaddingTopPx();

  const updatePanelTop = useCallback(() => {
    const headerBottom = headerRef.current?.getBoundingClientRect().bottom;
    setPanelTop(Math.max(headerBottom ?? pageOffsetTop, pageOffsetTop));
  }, [headerRef, pageOffsetTop]);

  // Measured again on open, and before paint: a page that renders a loading
  // state before it has a header mounts this with nothing to measure.
  useIsomorphicLayoutEffect(() => {
    updatePanelTop();
  }, [isOpen, updatePanelTop]);
  useEventListener('scroll', updatePanelTop);
  // A window `resize` covers what the observer below cannot: it keys its effect
  // on stable values and bails on a null ref, so a header absent at mount is
  // never picked up for the lifetime of the page.
  useEventListener('resize', updatePanelTop);
  useResizeObserver({ ref: headerRef, onResize: updatePanelTop });

  // `close()` nulls `options` synchronously; cache `exitImmediate` so
  // it survives into the close render.
  const lastExitImmediateRef = useRef(false);
  if (isOpen) {
    lastExitImmediateRef.current = !!options?.exitImmediate;
  }

  const container = containerRef.current;
  if (container) {
    container.style.setProperty(
      '--context-panel-width',
      isOpen ? (options?.width ?? DEFAULT_PANEL_WIDTH) : '0px',
    );
    container.style.setProperty(
      '--context-panel-margin-transition',
      !isOpen && lastExitImmediateRef.current ? '0s' : '0.3s ease',
    );
  }

  return (
    <ContextPanel.Outlet
      className={s.pageContextPanel}
      content={options?.content}
      exitImmediate={options?.exitImmediate ?? lastExitImmediateRef.current}
      header={options?.header}
      open={isOpen}
      padded={options?.padded}
      style={{ top: panelTop }}
      width={options?.width}
    />
  );
};
